import * as React from 'react'
import { ReactElement } from 'react'
import {
    Button,
    makeStyles,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
    Overflow,
    OverflowItem,
    OverflowItemProps,
    useIsOverflowItemVisible,
    useOverflowMenu,
} from '@fluentui/react-components'
import type { ObserveOptions } from '@fluentui/priority-overflow'
import { useTranslation } from 'react-i18next'

const useStyles = makeStyles({
    container: {
        width: '100%',
    },
})

type OverflowMenuProps = Partial<ObserveOptions> & {
    data?: string[]
    onItemClick?: (value: string) => void
}

type OverflowMenuItemProps = OverflowMenuProps &
    Pick<OverflowItemProps, 'id'> & {
        value: string
    }

function OverflowMenuItem(props: OverflowMenuItemProps): ReactElement {
    const isVisible = useIsOverflowItemVisible(props.id)

    if (isVisible) {
        return null
    }

    return <MenuItem onClick={() => props.onItemClick(props.value)}>{props.value}</MenuItem>
}

function OverflowMenu(props: OverflowMenuProps): ReactElement {
    const { t: translate } = useTranslation()
    const { ref, overflowCount, isOverflowing } = useOverflowMenu<HTMLButtonElement>()

    if (!isOverflowing) {
        return null
    }

    return (
        <Menu>
            <MenuTrigger disableButtonEnhancement>
                <MenuButton tabIndex={-1} ref={ref} appearance={'subtle'} size={'small'}>
                    +{translate('base.item.label', { count: overflowCount })}
                </MenuButton>
            </MenuTrigger>

            <MenuPopover>
                <MenuList>{props.data?.map((value, index) => <OverflowMenuItem key={index} id={`${index}`} value={value} {...props} />)}</MenuList>
            </MenuPopover>
        </Menu>
    )
}

export default function OverflowButtonPanel(props: OverflowMenuProps): ReactElement {
    const styles = useStyles()

    return (
        <Overflow minimumVisible={props.minimumVisible ?? 3}>
            <div className={styles.container}>
                {props.data?.map((value, index) => (
                    <OverflowItem key={index} id={`${index}`}>
                        <Button tabIndex={-1} appearance={'subtle'} size={'small'} onClick={() => props.onItemClick(value)}>
                            {value}
                        </Button>
                    </OverflowItem>
                ))}
                <OverflowMenu {...props} />
            </div>
        </Overflow>
    )
}
